import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import BaseShortcutActions from '../base/actions';
import { createThrottleDecorator, preventDefault, stopPropagation } from '../decorator';
import { SearchReplaceType } from '../../../search-replace-manager/search-replace-manager-v3.interface';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { InjectorService } from '@tencent/workbench';
import { IPcActionsInjectable, IPcShortcutActionsServices } from '../interface';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { transaction } from 'mobx';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWOperationType, TDWModuleType, TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { CommentShowAction, ShowActionSource } from '@src/js/constant';

export default class PcShortcutActions extends BaseShortcutActions {
    protected injectable?: IPcActionsInjectable;
    protected services: IPcShortcutActionsServices;
    private toggleFeature;

    public constructor(
        services: Partial<IPcShortcutActionsServices>,
        injectable?: IPcActionsInjectable,
    ) {
        super(services);
        this.services = services;
        this.injectable = injectable;
        import(/* webpackChunkName: "workbench" */ '@framework/workbench/ui/toggle/toggle-feature').then((module) => {
            const { ToggleFeature } = module;
            this.toggleFeature = ToggleFeature;
        })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    }
    /* 注释迁移
     * 以下几项理应是触发工具栏或者菜单栏的方法，但是目前两端均没有提供直接触发的方法和能力
     * 而这里通过快捷键处理的时候并不能拿到当前的状态，因此不能通过两端的方式直接触发回调方法
     * 因此使用比较hack的方式，之后工具栏提供对外方法了以后再通过类似的action方式触发
     */
    @stopPropagation
    @preventDefault
    public italic() {
        const toolbarData = this.menuManager?.toolbarMenu();
        if (!toolbarData) {
            return;
        }
        toolbarData.callback?.(MenuType.TOOLBAR, 'italic', !toolbarData.values?.italic);
    }

    @stopPropagation
    @preventDefault
    public bold() {
        const toolbarData = this.menuManager?.toolbarMenu();
        if (!toolbarData) {
            return;
        }
        toolbarData.callback?.(MenuType.TOOLBAR, 'bold', !toolbarData.values?.bold);
    }

    @stopPropagation
    @preventDefault
    public underline() {
        const toolbarData = this.menuManager?.toolbarMenu();
        if (!toolbarData) {
            return;
        }
        toolbarData.callback?.(MenuType.TOOLBAR, 'underLine', !toolbarData.values?.underLine);
    }

    @stopPropagation
    @preventDefault
    public 'delete-line'() {
        const toolbarData = this.menuManager?.toolbarMenu();
        if (!toolbarData) {
            return;
        }
        toolbarData.callback?.(MenuType.TOOLBAR, 'deleteLine', !toolbarData.values?.deleteLine);
    }

    @stopPropagation
    @preventDefault
    public async search() {
        if (!this.searchReplaceManager) {
            return;
        }
        if (this.historyManager?.isShown()) return;
        if (this.checkLoadingState()) {
            return;
        }
        const searchReplaceManager = await this.searchReplaceManager.promise();
        const type = searchReplaceManager.searchReplaceType;
        const openFlag = searchReplaceManager.dialogOpenFlag;
        if (!openFlag || type !== SearchReplaceType.Search) {
            searchReplaceManager.openDialog(SearchReplaceType.Search);
        }
    }

    @stopPropagation
    @preventDefault
    public async replace() {
        if (!this.searchReplaceManager) {
            return;
        }
        if (this.historyManager?.isShown()) return;
        if (this.checkLoadingState()) {
            return;
        }
        const searchReplaceManager = await this.searchReplaceManager.promise();
        const type = searchReplaceManager.searchReplaceType;
        const openFlag = searchReplaceManager.dialogOpenFlag;
        if (!openFlag || type !== SearchReplaceType.Replace) {
            searchReplaceManager.openDialog(SearchReplaceType.Replace);
        }
    }

    @stopPropagation
    @preventDefault
    public undo() {
        const toolbar = this.menuManager?.toolbarMenu();
        toolbar?.callback(MenuType.TOOLBAR, ActionType.UNDO);
    }

    @stopPropagation
    @preventDefault
    public redo() {
        const toolbar = this.menuManager?.toolbarMenu();
        toolbar?.callback(MenuType.TOOLBAR, ActionType.REDO);
    }

    public delete(event: KeyboardEvent) {
        if (this.checkTextEditing()) {
            return;
        }
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (this.checkEditorState()) {
            return;
        }
        if (this.checkLoadingState()) {
            return;
        }

        // 当选中动画条目时，通过keydown触发删除操作只删除动画，而非删除整页slide
        const { selectedAnims, selectionService } = this;
        const slideModel = selectionService?.currentSlide;
        if (slideModel && selectedAnims?.length) {
            selectedAnims.selectedArray.forEach((animModel) => {
                slideModel.removeAnimation(animModel);
            });
            selectedAnims.clear();
            return;
        }

        this.deleteInSlideOrEditorRegion();
    }

    public pageup(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }
        transaction(() => {
            if (!this.selectionService) {
                return;
            }
            const { currentSlideIndex } = this.selectionService;
            if (this.masterLayoutEditService?.isMasterEditType && currentSlideIndex >= 0) {
                this.selectionService.selectedSlides.clear();
                this.pageUpInMasterEditType(event);
            } else {
                this.normalPageUp(event);
            }
        });
    }

    public pagedown(event: KeyboardEvent) {
        if (this.checkIsInAnyEditingState()) {
            return;
        }
        if (!this.canChangeSelectedSlide()) {
            return;
        }
        transaction(() => {
            if (!this.selectionService) {
                return;
            }
            const { currentSlideIndex, selectedSlides } = this.selectionService;
            if (this.masterLayoutEditService?.isMasterEditType && currentSlideIndex >= 0) {
                selectedSlides.clear();
                this.pageDownInMasterEditType(event);
            } else {
                this.normalPageDown(event);
            }
        });
    }

    @stopPropagation
    @preventDefault
    public esc() {
        if (this.editingStateManager?.isCroping) {
            this.editingStateManager.updateElementCropState(false);
        }
        if (this.editingStateManager?.insertState.type !== null) {
            this.editingStateManager?.clearInsertState();
        }
        this.formatBrushManager?.onCancelBrush();
        this.animBrushManager?.promise().then((manager) => {
            manager?.onCancelBrush();
        });
    }

    public newSlideEnter(event: KeyboardEvent) {
        if (this.masterLayoutEditService?.isMasterEditType) {
            return;
        }
        if (this.checkLoadingState()) {
            return;
        }
        super.newSlideEnter(event);
    }
    public newSlideM(event: KeyboardEvent) {
        if (this.masterLayoutEditService?.isMasterEditType) {
            return;
        }
        if (this.checkLoadingState()) {
            return;
        }
        super.newSlideEnter(event);
    }


    // 无障碍快捷键开关
    @stopPropagation
    @preventDefault
    public accessibility() {
        this.accessibilityManager?.promise()
            .then((a11yManager) => {
                a11yManager.showAccessibilityModal(true);
            });
    }

    @stopPropagation
    @preventDefault
    public shortcutPanel() {
        this.services.shortcutPanelManager?.promise().then((module) => {
            module?.switchByShortcut?.();
        });
    }

    /**
     * 展示/隐藏参考线
     */
    @createThrottleDecorator(300)
    @stopPropagation
    @preventDefault
    public displayGuides() {
        this.menuManager?.guidesManager?.changeDisplayGuides();
    }
    /**
     * 展示/隐藏网格线
     */
    @createThrottleDecorator(300)
    @stopPropagation
    @preventDefault
    public displayGridLine() {
        this.menuManager?.gridLineManager?.changeDisplayGridLine();
    }

    /**
     * workbench版本的切换顶部栏隐藏，由于去掉了pc header，需要重写该部分快捷键
     */
    @createThrottleDecorator(300)
    public hideShowTopBar() {
        if (!this.toggleFeature) return;
        const toggleFeature = InjectorService.get(this.toggleFeature) as any;
        if (this.getToolbarStateManager()?.isHeaderFold) {
            toggleFeature.showMenu();
        } else {
            toggleFeature.hideMenu();
        }
    }

    @stopPropagation
    @preventDefault
    public link() {
        const model = this.selectionService?.getSelectedModel();
        if (!model) {
            return;
        }
        let tableCell;
        switch (model.type) {
            case UIModelType.SHAPE: // 文本，根据编辑状态唤起对应文本/图形链接编辑
                this.insertLinkByIsEditing();
                break;
            case UIModelType.IMAGE: // 图片，唤起图形链接编辑`
                // @ts-ignore
                this.menuManager._actionLink();
                break;
            case UIModelType.TABLE: // 表格，需要先取出表格被选中的单元
                tableCell = (model as ITableModel).getEditingElement();
                if (tableCell) {
                    tableCell.setTextLink();
                }
                break;
            case UIModelType.EMPTY: // 无选中，插入无状态的新建链接
                this.services.linkManager?.insertTextWithLink();
                break;
            default: // 未枚举类型、多选元素，不触发唤起
                return;
        }
    }


    @stopPropagation
    @preventDefault
    public blockCtrlS() {

    }

    @stopPropagation
    @preventDefault
    public print(event: KeyboardEvent) {
        this.services.printService?.handlePrint();
        tdw({
            opername: TDWOperationType.PRINT_PPT,
            module: TDWModuleType.SHORTCUT,
            action: TDWActionType.PRINT_ENTRANCE_CLICK,
        });
    }

    @stopPropagation
    @preventDefault
    public insertComment() {
        if (this.masterLayoutEditService?.isMasterEditType || !this.injectable?.showComment?.()) return;
        this.services.commentManager?.promise().then((commentManager) => {
            commentManager.setCommentShowAction(CommentShowAction.INSERT_COMMENT, ShowActionSource.SHORT_CUT);
        });
    }

    @stopPropagation
    @preventDefault
    protected normalPageUp(event: KeyboardEvent) {
        const { sectionManager } = this.services;
        if (sectionManager) {
            // 翻页时自动取消任意节的选中态
            sectionManager.selectedSectionIndex = -1;
        }
        super.normalPageUp(event);
    }

    @stopPropagation
    @preventDefault
    protected normalPageDown(event: KeyboardEvent) {
        const { sectionManager } = this.services;
        if (sectionManager) {
            const { selectedSectionIndex } = sectionManager;
            const selectedSection = this.uiDataService?.presentation.sectionList?.[selectedSectionIndex];
            // 下翻页时自动取消任意节的选中态
            sectionManager.selectedSectionIndex = -1;
            if (selectedSection?.numSlides === 0) {
                // 如果选中一个空节，则当前默认选中的是下一个有页面的节的第一页，因此下翻时并不需要更改页面选中态
                return;
            }
        }
        super.normalPageDown(event);
    }

    protected deleteInSlideOrEditorRegion() {
        if (this.selectionService?.lastMouseDownRegion === RegionType.SLIDELIST) {
            const { sectionManager } = this;
            if (sectionManager?.selectedSectionIndex !== undefined && sectionManager?.selectedSectionIndex !== -1) {
                const sectionProperty = this.uiDataService?.presentation.getSectionProperty();
                sectionProperty?.deleteSection(sectionManager.selectedSectionIndex, true);
                sectionManager.deleteSectionStateAt(sectionManager.selectedSectionIndex);
                return;
            }
        }
        super.deleteInSlideOrEditorRegion();
    }

    protected canChangeSelectedSlide() {
        // 使用isDrawerMenuShown变量时并不关心drawerStateManager是否初始化，使用快捷键时可能并未加载侧边栏
        return super.canChangeSelectedSlide()
        && !this.services.drawerStateManager?.dangerousInstance?.isDrawerMenuShown;
    }

    private get masterLayoutEditService() {
        return this.services.masterLayoutEditService;
    }

    private get formatBrushManager() {
        return this.services.formatBrushManager;
    }

    private get animBrushManager() {
        return this.services.animBrushManager;
    }

    private get searchReplaceManager() {
        return this.services.searchReplaceManager;
    }

    private get selectedAnims() {
        return this.services.selectedAnims;
    }

    private get accessibilityManager() {
        return this.services.accessibilityManager;
    }

    private get tipsManager() {
        return this.services.tipsManager;
    }

    private get sectionManager() {
        return this.services.sectionManager;
    }

    private get historyManager() {
        return this.services.historyManager;
    }

    /**
     * 根据是否正在编辑唤起对应的链接编辑
     * @param isEditing 是否正在编辑
     */
    private insertLinkByIsEditing() {
        const model = this.selectionService?.getSelectedModel();
        if (this.editorInstanceService?.checkEditorIsEditing()) {
            // @ts-ignore
            model?.editorInstance.setTextLink();
        } else {
            // 第三方内容不允许修改链接
            if (isIframeShapeModel(model)) return;
            // @ts-ignore
            this.menuManager._actionLink();
        }
    }


    private checkEditorState(): boolean {
        if (this.userPriviledgeService && this.userPriviledgeService?.pptEditorState > PPTEditorStateType.EDITABLE) {
            this.tipsManager?.showCannotEditTips();
            return true;
        }
        return false;
    }

    private checkLoadingState(): boolean {
        if (this.uiDataService?.presentation.operation.loading) {
            DUISnackbar.show({
                message: i18n.t('正在加载幻灯片，请稍后再试'),
                type: 'info',
                autoClose: true,
            });
            return true;
        }
        return false;
    }


    /**
     * 母版编辑下的向上翻页逻辑
     */
    private pageUpInMasterEditType(event: KeyboardEvent) {
        const curLayout = this.selectionService?.currentSlide;
        const curMaster = this.selectionService?.currentSlide?.getRelationMasterModel();
        const { layoutList } = curMaster;
        const isNotFirstMaster = this.uiDataService
            && this.uiDataService.masterModelList.findIndex(model => curMaster === model) > 0;
        if (this.selectionService?.currentSlideType === UIModelType.LAYOUT) {
            this.pageUpCurrentIsLayout(layoutList, curLayout, curMaster, event);
        } else if (this.selectionService?.currentSlideType === UIModelType.MASTER && isNotFirstMaster) {
            this.pageUpCurrentIsMaster(event);
        }
    }

    /**
     * 当前选中页是 master 页，向上翻页逻辑
     */
    private pageUpCurrentIsMaster(event: KeyboardEvent) {
        if (!this.selectionService || !this.uiDataService) {
            return;
        }
        const preMasterIndex = this.selectionService?.currentSlideIndex - 1;
        const preMaster = this.uiDataService.masterModelList[preMasterIndex];
        const preLayoutList = preMaster?.layoutList || [];
        if (preLayoutList.length) {
            const preLayout = preLayoutList[preLayoutList.length - 1];
            const preLayoutIndex = this.uiDataService?.layoutModelList
                .findIndex(layout => layout === preLayout);
            this.selectionService?.changeCurrentSlideIndexByUserAction(preLayoutIndex);
            this.selectionService?.updateCurrentSlideType(UIModelType.LAYOUT);
            this.selectionService?.selectedSlides.add(preLayout);
            this.editingStateManager && (this.editingStateManager.keyFlag4Moving = event.keyCode);
            event.preventDefault();
            event.stopPropagation();
        } else {
            this.normalPageUp(event);
        }
    }

    /**
     * 当前选中页是 layout 页，向上翻页逻辑
     */
    private pageUpCurrentIsLayout(layoutList, curLayout, curMaster, event: KeyboardEvent) {
        const isNotFirstLayout = layoutList.findIndex(layout => layout === curLayout) > 0;
        if (isNotFirstLayout) {
            this.normalPageUp(event);
            return;
        }
        if (!this.uiDataService || !this.selectionService) {
            return;
        }
        const masterIndex = this.uiDataService.masterModelList
            .findIndex(model => curMaster === model);
        this.selectionService.changeCurrentSlideIndexByUserAction(masterIndex);
        this.selectionService.updateCurrentSlideType(UIModelType.MASTER);
        this.selectionService.selectedSlides.add(curMaster);
        this.editingStateManager && (this.editingStateManager.keyFlag4Moving = event.keyCode);
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * 当前选中页是 master，向下翻页逻辑
     */
    private pageDownInMasterEditType(event: KeyboardEvent) {
        if (this.selectionService?.currentSlideType === UIModelType.MASTER) {
            const layoutList = (this.selectionService?.currentSlide as IMasterModel)?.layoutList;
            this.pageDownCurrentIsMaster(layoutList, event);
        } else if (this.selectionService?.currentSlideType === UIModelType.LAYOUT) {
            this.pageDownCurrentIsLayout(event);
        }
    }

    /**
     * 当前选中页是 layout，向下翻页逻辑
     */
    private pageDownCurrentIsLayout(event: KeyboardEvent) {
        const curLayout = this.selectionService?.currentSlide;
        const curMaster = curLayout?.getRelationMasterModel();
        const isNotLastLayout = curMaster.layoutList.findIndex(model => curLayout === model)
            < (curMaster.layoutList.length - 1);
        if (isNotLastLayout) {
            this.normalPageDown(event);
            return;
        }
        if (!this.uiDataService || !this.selectionService) {
            return;
        }
        const curMasterIndex = this.uiDataService
            .masterModelList.findIndex(model => curMaster === model);
        const isNotLastMaster = curMasterIndex < this.uiDataService.masterModelList.length - 1;
        const nextMasterIndex = curMasterIndex + 1;
        if (isNotLastMaster) {
            this.selectionService.changeCurrentSlideIndexByUserAction(nextMasterIndex);
            this.selectionService.updateCurrentSlideType(UIModelType.MASTER);
            this.selectionService.selectedSlides
                .add(this.uiDataService.masterModelList[nextMasterIndex]);
            this.editingStateManager && (this.editingStateManager.keyFlag4Moving = event.keyCode);
            event.preventDefault();
            event.stopPropagation();
        }
    }

    private pageDownCurrentIsMaster(layoutList, event: KeyboardEvent) {
        if (!layoutList.length) {
            this.normalPageDown(event);
            return;
        }
        if (!this.uiDataService || !this.selectionService) {
            return;
        }
        const nextLayoutIndex = this.uiDataService.layoutModelList
            .findIndex(layout => layout === layoutList[0]);
        this.selectionService.changeCurrentSlideIndexByUserAction(nextLayoutIndex);
        this.selectionService.updateCurrentSlideType(UIModelType.LAYOUT);
        this.selectionService.selectedSlides.add(layoutList[0]);
        this.editingStateManager && (this.editingStateManager.keyFlag4Moving = event.keyCode);
        event.preventDefault();
        event.stopPropagation();
    }
}
